import {
  Button,
  Card,
  Heading,
  Paragraph,
  Textfield,
} from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai/react";
import { atomWithStorage } from "jotai/utils";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";
import { env } from "@/env";
import type { FileRoutesByTo } from "@/routeTree.gen";

const hasAuthenticatedWithPassword = atomWithStorage<boolean>(
  "hasAuthenticatedWithPassword",
  false,
);

export const outsidePasswordProtectionPaths: (keyof FileRoutesByTo)[] = [
  "/",
  "/bestillinger",
  "/kundestøtte",
  "/vilkår",
  "/dokumentasjon/eksporter",
  "/dokumentasjon/innrapportering",
];

export const useShouldShowPasswordProtection = () => {
  const passwordIsRequired = Boolean(env.appPassword);
  const isAuthenticated = useAtomValue(hasAuthenticatedWithPassword);
  return {
    passwordIsRequired,
    isAuthenticated: isAuthenticated,
    hasAccess: passwordIsRequired && isAuthenticated,
  };
};

const passwordProtectionSchema = z.object({
  password: z
    .string()
    .min(1, "password-protection.feil.passord-pakrevd")
    .refine((value) => value === env.appPassword, {
      message: "password-protection.feil.ugyldig-passord",
    }),
});

export function PasswordProtection() {
  const { t } = useTranslation();
  const setHasAuthenticated = useSetAtom(hasAuthenticatedWithPassword);
  const passwordProtection = useShouldShowPasswordProtection();

  const form = useForm({
    values: {
      password: "",
    },
    resolver: zodResolver(passwordProtectionSchema),
  });

  function handleSubmit() {
    setHasAuthenticated(true);
  }

  if (passwordProtection.hasAccess) {
    return null;
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col items-center justify-center h-screen">
        <Card className="max-w-lg flex flex-col">
          <Heading>{t("password-protection.tittel")}</Heading>
          <Paragraph>{t("password-protection.beskrivelse")}</Paragraph>
          <Textfield
            label={t("password-protection.passord.label")}
            type="password"
            placeholder={t("password-protection.passord.placeholder")}
            error={
              form.formState.errors.password?.message &&
              t("password-protection.feil.ugyldig-passord")
            }
            {...form.register("password")}
          />
          <div className="flex justify-between">
            <Button className="self-start w-fit" variant="tertiary" asChild>
              <Link to={"/"}>{t("password-protection.tilbake")}</Link>
            </Button>
            <Button type="submit" className="self-end">
              {t("password-protection.neste")}
            </Button>
          </div>
        </Card>
      </div>
    </form>
  );
}
